import { z } from "zod";

// ---------------------------------------------------------------------------
// Find — the stylist-first men's haircut listing at find.openings.link
// (plans/find-listing-spec.md). A stylist's person-level profile lives at
// `/{handle}`; the listing at `/la/mens-haircut`.
// ---------------------------------------------------------------------------

/** Checked-out bookings a stylist needs before their first opt-in. */
export const FIND_ELIGIBILITY_THRESHOLD = 10;
/** Window, in days, the threshold counts over. */
export const FIND_ELIGIBILITY_WINDOW_DAYS = 30;

/** Stage 1 is Los Angeles; its days and times are read in this zone. */
export const FIND_MARKET_TIMEZONE = "America/Los_Angeles";
/** Days ahead the listing and profile search for openings. */
export const FIND_OPENINGS_DAYS = 7;
/**
 * Query parameter on a tenant member page link from a Find profile. The tenant
 * host keeps it in an HttpOnly cookie for the booking it leads to.
 */
export const FIND_LISTING_QUERY_PARAM = "find";
export const FIND_LISTING_COOKIE = "opn_find";

export const FIND_HANDLE_MIN = 3;
export const FIND_HANDLE_MAX = 30;

// Path segments the find host uses itself, words that would read as an
// official page, and every top-level route of the web app, so a handle can
// never shadow one there: `resolveFindRoute` passes reserved words through to
// the app (the PlatformShell footer links /terms-and-privacy). Markets ("la")
// are first segments too. Add a word here when the web app gains a top-level
// route.
const RESERVED_FIND_HANDLES = new Set([
  "about",
  "appointment",
  "blog",
  "book",
  "contact",
  "cookies",
  "dev",
  "feedback",
  "manage",
  "member",
  "photos",
  "quote",
  "requests",
  "services",
  "team",
  "terms-and-privacy",
  "la",
  "admin",
  "api",
  "app",
  "assets",
  "find",
  "help",
  "login",
  "openings",
  "privacy",
  "robots",
  "search",
  "signup",
  "sitemap",
  "static",
  "support",
  "terms",
]);

const FIND_HANDLE_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,28})[a-z0-9]$/;

/**
 * The stored form of a handle, or null when it cannot be one. Mirrors the
 * `StylistProfile_handle_shape` CHECK, plus no doubled hyphens and no
 * reserved word.
 */
export function normalizeFindHandle(value: string): string | null {
  const handle = value.trim().toLowerCase();
  if (!FIND_HANDLE_PATTERN.test(handle)) return null;
  if (handle.includes("--")) return null;
  if (RESERVED_FIND_HANDLES.has(handle)) return null;
  return handle;
}

/** A starting handle from a display name ("John Doe" → "john-doe"). */
export function suggestFindHandle(name: string): string {
  const base = name
    .normalize("NFKD")
    .replace(/[^\x00-\x7f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, FIND_HANDLE_MAX)
    .replace(/-+$/g, "");
  return normalizeFindHandle(base) ?? "";
}

export const findListingStatusSchema = z.enum(["UNLISTED", "LISTED", "PAUSED"]);
export type FindListingStatus = z.infer<typeof findListingStatusSchema>;

/** An average of 1–5 ratings (0 = not rated, never counted) and its count. */
export const findRatingSchema = z.object({
  average: z.number(),
  count: z.number().int(),
});
export type FindRating = z.infer<typeof findRatingSchema>;

// ── Staff: Settings → Openings profile ────────────────────────────────────

/** A member's own service. `up` is its own "starting at" flag ("$45+"). */
const findServiceSchema = z.object({
  id: z.string(),
  title: z.string(),
  price: z.number(),
  up: z.boolean(),
  durationMinutes: z.number().int(),
});

export const FIND_DISPLAY_NAME_MAX = 60;
export const FIND_BIO_MAX = 500;
export const FIND_INSTAGRAM_MAX = 30;

export const staffFindListingResponseSchema = z.object({
  /**
   * The member's business takes part in the listing. Stage 1 is Eddie's
   * Edition and Edition Studio only (spec 🔒 4); everything else is inert.
   */
  available: z.boolean(),
  eligibility: z.object({
    countedBookings: z.number().int(),
    threshold: z.number().int(),
    windowDays: z.number().int(),
    /** Met the threshold now, or already published once. */
    eligible: z.boolean(),
  }),
  /**
   * The session signed in with a text code to the phone that owns this
   * record's profile (or, with no profile linked yet, to any phone), so it
   * may write. A business-changed member phone never proves the owner.
   */
  phoneProven: z.boolean(),
  /**
   * This record's profile, or (`linked: false`) the profile another
   * business's record already made for the session's proven phone, which
   * saving links to.
   */
  profile: z
    .object({
      handle: z.string(),
      displayName: z.string(),
      photoUrl: z.string().nullable(),
      bio: z.string().nullable(),
      instagramHandle: z.string().nullable(),
      url: z.string(),
      suspended: z.boolean(),
      linked: z.boolean(),
    })
    .nullable(),
  /** The member's photo at this business, offered as the profile photo. */
  businessPhotoUrl: z.string().nullable(),
  link: z
    .object({
      listingStatus: findListingStatusSchema,
      listedServiceId: z.string().nullable(),
      optedInAt: z.string().nullable(),
    })
    .nullable(),
  /** The member's own services at this business, for the listed one. */
  services: z.array(findServiceSchema),
  suggestedHandle: z.string(),
});
export type StaffFindListingResponse = z.infer<
  typeof staffFindListingResponseSchema
>;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value.length ? value : null))
    .nullable()
    .optional();

export const staffFindListingUpdateSchema = z
  .object({
    handle: z.string().min(1).max(64),
    /** Required to publish; omitted keeps the listed one (or none). */
    serviceId: z.string().min(1).optional(),
    listed: z.boolean(),
    /**
     * Person-level profile fields. Omitted keeps the stored value; a new
     * profile starts from this business's member record.
     */
    displayName: z.string().trim().min(1).max(FIND_DISPLAY_NAME_MAX).optional(),
    bio: optionalText(FIND_BIO_MAX),
    instagramHandle: optionalText(FIND_INSTAGRAM_MAX).refine(
      (value) => value == null || /^@?[A-Za-z0-9._]{1,30}$/.test(value),
      { message: "Letters, numbers, dots and underscores only" },
    ),
    /** Replace the profile photo with this business's member photo. */
    usePhotoFromThisBusiness: z.boolean().optional(),
  })
  .strict();
export type StaffFindListingUpdate = z.input<
  typeof staffFindListingUpdateSchema
>;

export const staffFindHandleCheckResponseSchema = z.object({
  /** The stored form, or null when the value cannot be a handle. */
  handle: z.string().nullable(),
  /** Free, or already this stylist's own. */
  available: z.boolean(),
});
export type StaffFindHandleCheckResponse = z.infer<
  typeof staffFindHandleCheckResponseSchema
>;

// ── Public: listing and profile ───────────────────────────────────────────

const findLocationSchema = z.object({
  /** The business location (parent schedule) id. */
  scheduleId: z.string(),
  name: z.string(),
  city: z.string().nullable(),
  lat: z.number(),
  lng: z.number(),
});

export const findListingsQuerySchema = z.object({
  lat: z.coerce.number().min(-90).max(90).optional(),
  lng: z.coerce.number().min(-180).max(180).optional(),
  minPrice: z.coerce.number().min(0).optional(),
  maxPrice: z.coerce.number().min(0).optional(),
});
export type FindListingsQuery = z.infer<typeof findListingsQuerySchema>;

export const findListingItemSchema = z.object({
  handle: z.string(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
  onTime: findRatingSchema.nullable(),
  /** Up to three approved work photos, small variant. */
  workPhotos: z.array(z.string()),
  businessName: z.string(),
  /** The member's location nearest the viewer (first one without a viewer). */
  location: findLocationSchema.nullable(),
  distanceMiles: z.number().nullable(),
  service: findServiceSchema,
  /** The soonest bookable start in the next week, in `timezone`. */
  soonest: z
    .object({ date: z.string(), time: z.string(), timezone: z.string() })
    .nullable(),
});
export type FindListingItem = z.infer<typeof findListingItemSchema>;

export const findListingsResponseSchema = z.object({
  /** Nearest first with a viewer location, else by on-time score. */
  orderedBy: z.enum(["distance", "onTime"]),
  items: z.array(findListingItemSchema),
});
export type FindListingsResponse = z.infer<typeof findListingsResponseSchema>;

export const findReviewSchema = z.object({
  onTime: z.number().int().nullable(),
  quality: z.number().int().nullable(),
  /** Only comments the customer allowed on the public profile. */
  comment: z.string().nullable(),
  businessName: z.string(),
  /** "2026-10" — the month only, never the day. */
  month: z.string(),
});
export type FindReview = z.infer<typeof findReviewSchema>;

export const findProfileResponseSchema = z.object({
  handle: z.string(),
  displayName: z.string(),
  photoUrl: z.string().nullable(),
  bio: z.string().nullable(),
  instagramHandle: z.string().nullable(),
  onTime: findRatingSchema.nullable(),
  quality: findRatingSchema.nullable(),
  workPhotos: z.array(z.string()),
  /** Businesses where the stylist is listed now, one booking card each. */
  workplaces: z.array(
    z.object({
      salonId: z.string(),
      teamMemberId: z.string(),
      businessName: z.string(),
      /** Tenant subdomain; the booking link is {slug}.openings.link/member/{id}. */
      businessSlug: z.string().nullable(),
      locations: z.array(findLocationSchema),
      service: findServiceSchema,
      /** Up to three days with openings this week, in `timezone`. */
      openings: z.array(z.object({ date: z.string(), times: z.array(z.string()) })),
      timezone: z.string(),
    }),
  ),
  reviews: z.array(findReviewSchema),
});
export type FindProfileResponse = z.infer<typeof findProfileResponseSchema>;

/** Handles with a bookable listing, for the sitemap; no openings are read. */
export const findHandlesResponseSchema = z.object({
  handles: z.array(z.string()),
});
export type FindHandlesResponse = z.infer<typeof findHandlesResponseSchema>;
